import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon, WalletIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
export function ClassicGuaranteePrice() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const onCta = (e: React.MouseEvent) => {
    e.preventDefault();
    track('price_cta_click', {
      variant: 'classic'
    });
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-677" data-mp="3v13t4k1b1x7d">
      <div className="absolute inset-0 pointer-events-none" data-id="element-678" data-mp="2np4sebh973of">
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-50/40 blur-3xl hidden sm:block" data-id="element-679" data-mp="2jmhym510v8ol" />
        <div className="absolute top-0 left-0 w-[300px] h-[300px] rounded-full bg-cyan-50/25 blur-3xl hidden sm:block" data-id="element-680" data-mp="3s52meccay9bx" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-681" data-mp="yj3e9wbbd2j">
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-6" data-id="element-682" data-mp="3j4e77eb6a2xg">
          {/* Guarantee */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55
        }} className="flex flex-col p-7 lg:p-10 rounded-2xl bg-navy-50/70 border border-navy-200" data-id="element-683" data-mp="3n4obmmjbgla7">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-600" data-id="element-684" data-mp="2zhq0m4dyu5jo">
              <ShieldCheckIcon className="w-6 h-6" data-id="element-685" data-mp="2qg04wmd1wkg3" />
            </div>
            <h2 className="mt-6 text-2xl lg:text-3xl font-extrabold text-navy-900 tracking-tight leading-tight" data-id="element-686" data-mp="2qgs0hx6nei6d">
              {c.guarantee.title}
            </h2>

            <div className="mt-8 pt-7 border-t border-navy-200" data-id="element-687" data-mp="10zwia8hn0eht">
              <div className="text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight leading-none" data-id="element-688" data-mp="w8ei3mnfuhxw">
                {c.guarantee.value}
              </div>
              <div className="mt-2 text-sm text-navy-500" data-id="element-689" data-mp="r9bf07r0qlsk">
                {c.guarantee.valueLabel}
              </div>
            </div>

            <p className="mt-6 text-base text-navy-500 leading-relaxed" data-id="element-690" data-mp="1jxgx0mqotwop">
              {c.guarantee.text}
            </p>
          </motion.div>

          {/* Price */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="flex flex-col p-7 lg:p-10 rounded-2xl bg-white border border-navy-200 shadow-xl shadow-navy-200/30" data-id="element-691" data-mp="1ewjeua5oh2n7">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 text-primary-600" data-id="element-692" data-mp="3qo6odvfm79mt">
              <WalletIcon className="w-6 h-6" data-id="element-693" data-mp="267pe7os7gfwa" />
            </div>
            <h2 className="mt-6 text-2xl lg:text-3xl font-extrabold text-navy-900 tracking-tight leading-tight" data-id="element-694" data-mp="2b4frk4cmkxe0">
              {c.price.title}
            </h2>

            <p className="mt-6 text-base font-semibold text-navy-800" data-id="element-695" data-mp="2f4zop7x78105">
              {c.price.lead}
            </p>
            <ul className="mt-4 space-y-2.5" data-id="element-696" data-mp="2owm4zioe8q9r">
              {c.price.factors.map(f => <li key={f} className="flex items-start gap-3 text-base text-navy-600" data-id="element-697" data-mp="2tvqg4euvoy2q">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 mt-0.5 rounded-md bg-primary-50 text-primary-600" data-id="element-698" data-mp="2yvuyybj9e4w4">
                    <CheckIcon className="w-3 h-3" data-id="element-699" data-mp="32u2ej31zl05q" />
                  </span>
                  {f}
                </li>)}
            </ul>

            <p className="mt-6 text-sm text-navy-500 leading-relaxed" data-id="element-700" data-mp="18jfbf7klho44">
              {c.price.note}
            </p>

            <div className="mt-auto pt-8" data-id="element-701" data-mp="d6mhgqoycqdo">
              <a href="#contact" onClick={onCta} className="group inline-flex w-full items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-colors shadow-lg shadow-primary-600/25" data-id="element-702" data-mp="96vuky2jquef">
                {c.price.cta}
                <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-703" data-mp="3loiw6culb1aj" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}